import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import Select from 'react-select';
import apiProvider from "../apiProvider/product"
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const ProductReportLayer = () => {
    const formatDate = (date) => {
        const d = new Date(date);
        let month = '' + (d.getMonth() + 1);
        let day = '' + d.getDate();
        const year = d.getFullYear();

        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;

        return [year, month, day].join('-');
    };

    const today = new Date();
    const tenDaysAgo = new Date();
    tenDaysAgo.setDate(today.getDate() - 10);

    const [formData, setFormData] = useState({
        productId: "",
        customerId: "",
        fromDate: tenDaysAgo,
        toDate: today
    });
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [productList, setProductlist] = useState([]);
    const [customerList, setCustomerList] = useState([]);
    const [isSearched, setIsSearched] = useState(false);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    const fetchProducts = async () => {
        try {
            const input = {
                page: 0,
                limit: 2000
            }
            const response = await apiProvider.getAllproductList(input);

            if (response.status) {
                setProductlist(response.response?.data || []);
            } else {
                console.error("failed to fetch product list")
            }
        } catch (error) {
            console.error("error fetching product list :", error);
        }
    }

    const fetchCustomers = async () => {
        try {
            const input = {
                page: 0,
                limit: 2000
            }
            const response = await apiProvider.getCustomerList(input);
            if (response.status) {
                setCustomerList(response.response?.data || [])
            } else {
                console.error("failed to fetch customer list")
            }
        } catch (error) {
            console.error("error fetching customer list :", error)
        }
    }

    const fetchReport = async () => {
        setLoading(true);
        try {
            const input = {
                page: pageIndex,
                limit: pageSize,
                fromDate: formatDate(formData.fromDate),
                toDate: formatDate(formData.toDate),
                productId: formData.productId || undefined,
                customerId: formData.customerId || undefined
            };
            console.log("input :", input);
            const result = await apiProvider.getProductOrderList(input);

            if (result.status && result.response) {
                setReportData(result.response.data || []);
                setTotalPages(result.response.totalPages || 1);
                setTotalRecords(result.response.total || 0);
                setIsSearched(true);
            } else {
                setReportData([]);
                setIsSearched(true);
                toast.error("Failed to fetch report data");
            }
        } catch (error) {
            console.error("Error fetching report:", error);
            toast.error("An error occurred while fetching the report");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (isSearched) {
            fetchReport();
        }
    }, [pageIndex]);

    useEffect(() => {
        fetchCustomers();
        fetchProducts();
    }, [])



    const handleChange = (name, value) => {
        console.log("value :", value);
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        setPageIndex(0);
        fetchReport();
    };

    const handleExportExcel = () => {
        if (reportData.length === 0) {
            toast.info("No data available to export");
            return;
        }

        const dataToExport = reportData.map((item, index) => ({
            "S.No": index + 1,
            "Date": dayjs(item.date).format('DD/MM/YYYY'),
            "Order ID": item.orderId,
            "Customer Name": item.customerName,
            "Product Name": item.productName,
            "Variant Name": item.variantName || '-',
            "Quantity": item.quantity,
            "Unit Price": item.unitPrice,
            "Total Price": item.totalPrice
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);

        // Set column widths
        const wscols = [
            { wch: 6 },  // S.No
            { wch: 15 }, // Date
            { wch: 15 }, // Order ID
            { wch: 25 }, // Customer Name
            { wch: 30 }, // Product Name
            { wch: 20 }, // Variant Name
            { wch: 10 }, // Quantity
            { wch: 15 }, // Unit Price
            { wch: 15 }  // Total Price
        ];
        worksheet['!cols'] = wscols;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Product Sales Report");

        const fileName = `Product_Sales_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        toast.success("Excel report downloaded successfully!");
    };

    const customSelectStyles = {
        control: (base) => ({
            ...base,
            borderRadius: '6px',
            borderColor: '#e2e8f0',
            boxShadow: 'none',
            '&:hover': {
                borderColor: '#cbd5e1'
            }
        }),
        placeholder: (base) => ({
            ...base,
            color: '#94a3b8'
        }),
        menu: (base) => ({
            ...base,
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            zIndex: 100
        }),
        option: (base, { isFocused, isSelected }) => ({
            ...base,
            backgroundColor: isSelected
                ? primaryColor
                : isFocused
                    ? '#f1f5f9'
                    : 'transparent',
            color: isSelected ? '#fff' : '#1e293b',
            cursor: 'pointer',
            padding: '10px 12px',
            '&:active': {
                backgroundColor: isSelected ? primaryColor : '#e2e8f0'
            }
        }),
        singleValue: (base) => ({
            ...base,
            color: '#1e293b'
        })
    };

    const primaryColor = "#1e293b";

    return (
        <div className="card h-100 p-0 radius-12 overflow-hidden border shadow-sm">
            {/* <div className="card-header border-bottom bg-white ">
                <p className="mb-0 fw-bold" style={{ color: primaryColor, fontSize: '20px' }}>Product Sales Report</p>
            </div> */}

            <div className="card-body">
                {/* Improved Report Filters Section */}
                <div className="mx-auto" style={{ maxWidth: '600px', width: '100%' }}>
                    <div className="text-center mb-20 py-3">
                        <h6 className="mb-0 fw-bold" style={{ color: primaryColor, fontSize: '18px' }}>Report Filters</h6>
                    </div>

                    <form onSubmit={handleSearch} className="bg-white p-6 radius-12">
                        <div className="mb-16">
                            <label className="form-label fw-bold text-secondary mb-4" style={{ fontSize: '14px' }}>From Date <span className="text-danger">*</span></label>
                            <DatePicker
                                selected={formData.fromDate}
                                onChange={(date) => handleChange('fromDate', date)}
                                dateFormat="dd/MM/yyyy"
                                className="form-control border-gray-200 radius-6 py-8 w-100"
                                wrapperClassName="w-100"
                                placeholderText="DD/MM/YYYY"
                                required
                            />
                        </div>
                        <div className="mb-16">
                            <label className="form-label fw-bold text-secondary mb-4" style={{ fontSize: '14px' }}>To Date <span className="text-danger">*</span></label>
                            <DatePicker
                                selected={formData.toDate}
                                onChange={(date) => handleChange('toDate', date)}
                                dateFormat="dd/MM/yyyy"
                                className="form-control border-gray-200 radius-6 py-8 w-100"
                                wrapperClassName="w-100"
                                placeholderText="DD/MM/YYYY"
                                required
                            />
                        </div>
                        <div className="mb-16">
                            <label className="form-label fw-bold text-secondary mb-4" style={{ fontSize: '14px' }}>Select Product</label>
                            <Select
                                options={productList.map(p => ({ value: p._id, label: `${p.productName} (${p.productCode})` }))}
                                value={formData.productId ? {
                                    value: formData.productId,
                                    label: productList.find(p => p._id === formData.productId)?.productName || 'Select Product'
                                } : null}
                                onChange={(opt) => handleChange('productId', opt?.value || "")}
                                isSearchable
                                isClearable
                                placeholder="All Products"
                                styles={{
                                    ...customSelectStyles,
                                    control: (base) => ({
                                        ...customSelectStyles.control(base),
                                        minHeight: '42px',
                                        fontSize: '16px',
                                        borderRadius: '6px ',
                                    })
                                }}
                            />
                        </div>
                        <div className="mb-24">
                            <label className="form-label fw-bold text-secondary mb-4" style={{ fontSize: '14px' }}>Select Customer</label>
                            <Select
                                options={customerList.map(c => ({ value: c._id, label: `${c.name} (${c.email})` }))}
                                value={formData.customerId ? {
                                    value: formData.customerId,
                                    label: customerList.find(c => c._id === formData.customerId)?.name || 'Select Customer'
                                } : null}
                                onChange={(opt) => handleChange('customerId', opt?.value || "")}
                                isSearchable
                                isClearable
                                placeholder="All Customers"
                                styles={{
                                    ...customSelectStyles,
                                    control: (base) => ({
                                        ...customSelectStyles.control(base),
                                        minHeight: '42px',
                                        fontSize: '16px',
                                        borderRadius: '6px ',
                                    })
                                }}
                            />
                        </div>
                        <div className="text-center">
                            <button
                                type="submit"
                                className="btn w-100 py-10 radius-8 text-white fw-bold d-inline-flex align-items-center justify-content-center gap-2"
                                style={{ backgroundColor: primaryColor, border: 'none', transition: 'all 0.2s', fontSize: '14px' }}
                                disabled={loading}
                            >
                                <Icon icon="ic:baseline-search" className="text-xl" />
                                {loading ? "Searching..." : "Search Report"}
                            </button>
                        </div>
                    </form>
                </div>

                {/* <div className="divider mb-32 border-bottom"></div> */}

                {/* Actions & Report results */}
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-20">
                    <h6 className="mb-0 fw-bold">Sales Summary Results</h6>
                    <button
                        className="btn radius-8 text-white d-flex align-items-center gap-2 px-20 py-10"
                        style={{ backgroundColor: primaryColor, border: 'none' }}
                        onClick={handleExportExcel}
                        disabled={reportData.length === 0}
                    >
                        <Icon icon="mdi:file-excel" className="text-xl" />
                        Export Excel
                    </button>
                </div>

                {/* Report Table */}
                <div
                    className="table-responsive scroll-sm border-0 radius-12 shadow-sm"
                    style={{ maxHeight: '600px', overflowY: 'auto', overflowX: 'auto' }}
                >
                    <table className="table mb-0" style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', minWidth: '1000px' }}>
                        <thead className="position-sticky top-0" style={{ zIndex: 10 }}>
                            <tr>
                                <th scope="col" className="text-white fw-bold py-20 px-24 border-0 text-center" style={{ backgroundColor: primaryColor, position: 'sticky', top: 0 }}>S.No</th>
                                <th scope="col" className="text-white fw-bold py-20 px-24 border-0" style={{ backgroundColor: primaryColor, position: 'sticky', top: 0 }}>Date</th>
                                <th scope="col" className="text-white fw-bold py-20 px-24 border-0" style={{ backgroundColor: primaryColor, position: 'sticky', top: 0 }}>Order ID</th>
                                <th scope="col" className="text-white fw-bold py-20 px-24 border-0" style={{ backgroundColor: primaryColor, position: 'sticky', top: 0 }}>Customer Name</th>
                                <th scope="col" className="text-white fw-bold py-20 px-24 border-0" style={{ backgroundColor: primaryColor, position: 'sticky', top: 0 }}>Product Name</th>
                                <th scope="col" className="text-white fw-bold py-20 px-24 border-0" style={{ backgroundColor: primaryColor, position: 'sticky', top: 0 }}>Variant Name</th>
                                <th scope="col" className="text-white fw-bold py-20 px-24 border-0" style={{ backgroundColor: primaryColor, position: 'sticky', top: 0 }}>Quantity</th>
                                <th scope="col" className="text-white fw-bold py-20 px-24 border-0 text-end" style={{ backgroundColor: primaryColor, position: 'sticky', top: 0 }}>Unit Price</th>
                                <th scope="col" className="text-white fw-bold py-20 px-24 border-0 text-end" style={{ backgroundColor: primaryColor, position: 'sticky', top: 0 }}>Total Price</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white">
                            {isSearched && reportData.length > 0 ? (
                                reportData.map((item, index) => (
                                    <tr key={index} className="border-bottom hover-bg-light" style={{ transition: 'background 0.2s' }}>
                                        <td className="py-16 px-24 text-center fw-medium text-secondary">{pageIndex * pageSize + index + 1}</td>
                                        <td className="py-16 px-24 text-secondary">{dayjs(item.date).format('DD/MM/YYYY')}</td>
                                        <td className="py-16 px-24 fw-medium">{item.orderId}</td>
                                        <td className="py-16 px-24 text-secondary">{item.customerName}</td>
                                        <td className="py-16 px-24">{item.productName}</td>
                                        <td className="py-16 px-24 text-center">{item.variantName || '-'}</td>
                                        <td className="py-16 px-24 text-center">{item.quantity}</td>
                                        <td className="py-16 px-24 text-end text-secondary">{item.currencySymbol}{Number(item.unitPrice || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                        <td className="py-16 px-24 text-end fw-bold" style={{ color: primaryColor }}>{item.currencySymbol}{Number(item.totalPrice || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="9" className="text-center py-60 text-muted bg-white">
                                        <div className="d-flex flex-column align-items-center opacity-50">
                                            <Icon icon="mdi:database-search-outline" style={{ fontSize: '48px' }} className="mb-12" />
                                            {isSearched ? "No matching records found for the selected timeframe." : "Configure filters and search to generate your report."}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {isSearched && reportData.length > 0 && (
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24 px-12 pb-12">
                        <span className="text-secondary-light">
                            Showing {pageIndex * pageSize + 1} to {Math.min((pageIndex + 1) * pageSize, totalRecords)} of {totalRecords} entries
                        </span>
                        <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center mb-0">
                            <li className={`page-item ${pageIndex === 0 ? 'disabled' : ''}`}>
                                <button
                                    className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                    onClick={() => setPageIndex(p => Math.max(0, p - 1))}
                                    disabled={pageIndex === 0}
                                    style={{ cursor: pageIndex === 0 ? 'not-allowed' : 'pointer', border: 'none' }}
                                >
                                    <Icon icon="ep:d-arrow-left" />
                                </button>
                            </li>

                            {[...Array(totalPages)].map((_, i) => {
                                if (
                                    i === 0 ||
                                    i === totalPages - 1 ||
                                    (i >= pageIndex - 1 && i <= pageIndex + 1)
                                ) {
                                    return (
                                        <li key={i} className="page-item">
                                            <button
                                                className={`page-link fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${pageIndex === i ? 'bg-primary-600 text-white' : 'bg-neutral-200 text-secondary-light'}`}
                                                onClick={() => setPageIndex(i)}
                                                style={{ border: 'none' }}
                                            >
                                                {i + 1}
                                            </button>
                                        </li>
                                    );
                                } else if (
                                    (i === 1 && pageIndex > 2) ||
                                    (i === totalPages - 2 && pageIndex < totalPages - 3)
                                ) {
                                    return <li key={i} className="px-2">...</li>;
                                }
                                return null;
                            })}

                            <li className={`page-item ${pageIndex + 1 >= totalPages ? 'disabled' : ''}`}>
                                <button
                                    className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                    onClick={() => setPageIndex(p => Math.min(totalPages - 1, p + 1))}
                                    disabled={pageIndex + 1 >= totalPages}
                                    style={{ cursor: pageIndex + 1 >= totalPages ? 'not-allowed' : 'pointer', border: 'none' }}
                                >
                                    <Icon icon="ep:d-arrow-right" />
                                </button>
                            </li>
                        </ul>
                    </div>
                )}
            </div>
            <ToastContainer />
        </div>
    );
};

export default ProductReportLayer;
